<script lang="ts">
    import { t } from "@lernetz/svelte-translate";
    import PupilsNumber from "./PupilsNumber.svelte";
    import { Icon } from "@lernetz/svelte-icon";
    import { router } from "@inertiajs/svelte";
    import { setupSharedProps, sharedProps } from "../../setup";
    import ResultsOverlay from "../ResultsOverlay.svelte";

    export let onClose: () => void;
    export let dashboard;
    export let type;

    setupSharedProps();

    let preview = false;
    let enabled = false;
    let showPupilsNumber = false;
    let priorResults = dashboard.priorResults;
    let afterResults = dashboard.afterResults;
    let priorRight = 0;
    let priorWrong = 0;
    let afterRight = 0;
    let afterWrong = 0;
    let openPriorResultsOverlay = false;
    let openAfterResultsOverlay = false;

    if(type == "prior") {
        enabled = dashboard.lockPrior;
    }
    else {
        enabled = dashboard.lockAfter;
    }

    function aggregateResults(results) {
        const map = new Map();
        for (const result of results) {
            const rawId = result.question?.id ?? result.question?.['$oid'];
            const id = rawId?.$oid ?? String(rawId ?? '');
            if (!id) continue;
            if (map.has(id)) {
                map.get(id).right += result.right;
                map.get(id).wrong += result.wrong;
            } else {
                map.set(id, { ...result, right: result.right, wrong: result.wrong });
            }
        }
        return Array.from(map.values());
    }

    priorResults = aggregateResults(priorResults);
    afterResults = aggregateResults(afterResults);

    priorResults.forEach( result => {
        priorRight += result.right;
        priorWrong += result.wrong;
    })

    afterResults.forEach( result => {
        afterRight += result.right;
        afterWrong += result.wrong;
    })

    let priorPpl = priorResults.length > 0
        ? priorResults.filter(r => r.question.id === priorResults[0].question.id)
                      .reduce((sum, r) => sum + r.right + r.wrong, 0)
        : 0;
    let afterPpl = afterResults.length > 0
        ? afterResults.filter(r => r.question.id === afterResults[0].question.id)
                      .reduce((sum, r) => sum + r.right + r.wrong, 0)
        : 0;

    let priorPercent = Math.round(priorRight / ( priorRight + priorWrong) * 100) | 0;
    let afterPercent = Math.round(afterRight / ( afterRight + afterWrong) * 100) | 0;

    function openPupilsNumber() {
        showPupilsNumber = true;
    }

    function closePupilsNumber() {
        showPupilsNumber = false;
    }

    function toggleLock() {
        enabled = !enabled;
        let data = {dashboardId:dashboard.id, type: type, locked: enabled}
        if(type == "prior") {
            dashboard.lockPrior = enabled;
            router.post('/dashboardLock', data)
        }
        else {
            dashboard.lockAfter = enabled;
            router.post('/dashboardLock', data)
        }
    }

    function openPriorResults() {
      openPriorResultsOverlay = true;
    }

    function openAfterResults() {
      openAfterResultsOverlay = true;
    }

</script>
{#if openPriorResultsOverlay}
  <ResultsOverlay title="results prior knowledge" results={priorResults} ppl={priorPpl} onClose={() => openPriorResultsOverlay = false} />
{/if}

{#if openAfterResultsOverlay}
  <ResultsOverlay title="results after knowledge" results={afterResults} ppl={afterPpl} onClose={() => openAfterResultsOverlay = false} />
{/if}
<div class="fixed inset-0 {dashboard.cycle == 'cycle1' ? 'bg-yellow-light' : 'bg-green-light'} flex flex-col items-center p-16 md:p-24 xl:p-40 z-20 overflow-y-auto">
    {#if showPupilsNumber}
      <PupilsNumber onClose="{closePupilsNumber}" {preview} {dashboard} {type}></PupilsNumber>
    {/if}
    <div class="w-full flex max-w-1280 justify-between items-start gap-12">
      <div class="flex gap-12 md:gap-24 items-start">
        <button on:click="{onClose}" class="w-48 h-48 bg-blue-main text-white rounded-md z-10 shrink-0">
          <Icon name="arrow-circle-left" class="w-28 h-28 place-self-center"></Icon>
        </button>
        <div class="text-28 md:text-36 xl:text-48 font-bold font-Caveat text-blue-main">
          {#if type == "prior"}
          {$t('Testing prior knowledge - Plenary session')}
          {:else}
          {$t('Testing after knowledge - Plenary session')}
          {/if}
        </div>
      </div>
        <Icon name="{sharedProps.currentLocale == 'de' ? 'logo-element-hero' : 'logo-element-hero-fr'}" class="w-96 md:w-128 xl:w-160 shrink-0 -mt-24 md:-mt-48"></Icon>
    </div>
    <div class="flex flex-col md:flex-row gap-16 md:gap-24 max-w-1280 w-full mt-24">
        <div class="basis-full md:basis-2/3 flex flex-col gap-16 md:gap-24">
            <div class="bg-white rounded-3xl p-24">
                <span class="font-bold text-22">{$t('Going through the questions with the whole class')}</span>
                <div class="text-18 flex flex-row gap-8 mt-16">
                    <div class="font-bold bg-yellow-main w-32 h-32 text-center place-content-center rounded-full text-white shrink-0">1</div>
                    <div>{$t('Start query and project questions via projector')}</div>
                </div>
                <div class="text-18 flex flex-row gap-8 mt-16">
                    <div class="font-bold bg-yellow-main w-32 h-32 text-center place-content-center rounded-full text-white shrink-0">2</div>
                    <div>{$t('Enter the corresponding number of students in the answers and evaluate the question.')}</div>
                </div>
                <div class="text-18 flex flex-row gap-8 mt-16">
                    <div class="font-bold bg-yellow-main w-32 h-32 text-center place-content-center rounded-full text-white shrink-0">3</div>
                    <div>{$t('Resolve the question, discuss it briefly if necessary, and then move on to the next question.')}</div>
                </div>
                <div class="text-18 flex flex-row gap-8 mt-16">
                    <div class="font-bold bg-yellow-main w-32 h-32 text-center place-content-center rounded-full text-white shrink-0">4</div>
                    <div>{$t('Display the answer, discuss briefly if necessary, and move on to the next question. ')}</div>
                </div>
            </div>
            <div class="bg-white rounded-3xl p-24">
                <div class="text-22 font-bold w-full border-b border-line-subtle pb-12">{$t('results')}</div>
                {#if priorResults.length == 0 && afterResults.length == 0}
              <div class="bg-line-subtle px-24 py-12 text-grey-1 rounded-md mt-16">
                <div class="">{$t('No results yet.')}</div>
                <div class="">{$t('Once the tests have been completed, the results will be displayed here.')}</div>
              </div>
              <div class="flex gap-8 mt-10">
                <Icon name="info" class="w-28 h-28 place-self-center"></Icon>
                <div class="">{$t('You are free to perform only one of the tests.')}</div>
              </div>
              {:else}
                <div class="">
                  <div>
                    {#if type == "prior"}
                        {#if priorResults.length == 0}
                        <div class="bg-line-subtle text-grey-1 text-center items-center flex rounded-md h-84 mt-8 w-full sm:w-256 justify-center">
                            {$t('no results')}
                        </div>
                        {:else}
                            <div class="flex mt-16 place-content-between flex-wrap gap-12">
                                <div class="flex gap-1">
                                    <div class="flex gap-8 bg-blue-light rounded-l-md py-24 px-16">
                                        <div class="font-bold text-22">{priorPpl}</div>
                                        <div class="text-18">SuS</div>
                                    </div>
                                    <div class="flex bg-blue-light rounded-r-md py-12 px-16 gap-32 items-center">
                                        <div class="pl-8 flex gap-8">
                                            <div class="text-28 font-bold">{priorPercent}%</div>
                                            <div class="text-18">{$t('correct')}</div>
                                        </div>
                                    </div>
                                </div>
                                <button class="bg-blue-main p-8 rounded-md h-fit flex gap-12 py-10 px-24 place-self-end" on:click={openPriorResults}>
                                    <div class="text-white text-18 font-bold">{$t('show results')}</div>
                                    <Icon name="arrow-up-right" class="w-28 h-28 place-self-center"></Icon>
                                </button>
                            </div>
                        {/if}
                    {/if}
                  </div>
                  <div>
                    {#if type == "after"}
                        {#if afterResults.length == 0}
                        <div class="bg-line-subtle text-grey-1 text-center items-center flex rounded-md h-84 mt-8 w-full sm:w-256 justify-center">
                            {$t('no results')}
                        </div>
                        {:else}
                        <div class="flex mt-16 place-content-between flex-wrap gap-12">
                            <div class="flex gap-1">
                                <div class="flex gap-8 bg-blue-light rounded-l-md py-24 px-16">
                                    <div class="font-bold text-22">{afterPpl}</div>
                                    <div class="text-18">SuS</div>
                                </div>
                                <div class="flex bg-blue-light rounded-r-md py-12 px-16 gap-32 items-center">
                                    <div class="pl-8 flex gap-8">
                                        <div class="text-28 font-bold">{afterPercent}%</div>
                                        <div class="text-18">{$t('correct')}</div>
                                    </div>
                                </div>
                            </div>
                            <button class="bg-blue-main p-8 rounded-md h-fit flex gap-12 py-10 px-24 place-self-end" on:click={openAfterResults}>
                                <div class="text-white text-18 font-bold">{$t('show results')}</div>
                                <Icon name="arrow-up-right" class="w-28 h-28 place-self-center"></Icon>
                            </button>
                        </div>
                        {/if}
                    {/if}
                  </div>
                </div>
              {/if}
            </div>
        </div>
        <div class="basis-full md:basis-1/3 flex flex-col gap-16 md:gap-24">
            <div class="bg-white rounded-3xl p-24">
                <div class="text-18 font-bold text-black flex gap-8">
                <div><Icon name="lock" class="w-28 h-28 place-self-center"></Icon></div>
                    <div class="grow">
                        {#if (type == "prior" && dashboard.lockPrior) || (type == "after" && dashboard.lockAfter)}
                            {$t('test locked')}
                        {:else}
                            {$t('lock test')}
                        {/if}
                    </div>
                    <div>
                        <label class="flex items-center gap-3 cursor-pointer">
                            <div
                                class="relative rounded-full transition-all duration-300 w-48 h-28 {enabled ? 'bg-blue-main' : 'bg-blue-light'}"
                                on:click={() => toggleLock()}
                            >
                                <div
                                class="absolute bg-white rounded-full shadow transition-all duration-300"
                                style="
                                    width:20px;
                                    height:20px;
                                    top:4px;
                                    left:{enabled ? '24px' : '4px'};
                                "
                                ></div>
                            </div>
                        </label>
                    </div>
                </div>
                {#if (type == "prior" && dashboard.lockPrior) || (type == "after" && dashboard.lockAfter)}
                    <div class="text-16 px-10 py-8 rounded-lg mt-12 bg-yellow-main">{$t('Students cannot take a test')}</div>
                {/if}
            </div>
            <div class="bg-white rounded-3xl p-24">
                <div class="text-18 font-bold text-yellow-extra border-b border-line-subtle w-full pb-10">{$t('tipps')}</div>
                <div class="text-18 font-bold mt-16">{$t('half-classes')}</div>
                <div class="text-16">{$t('If you want to do the quiz in several rounds, just start the prior knowledge test again. The number of rounds will then be added up automatically.')}</div>
                <div class="text-18 font-bold mt-16">{$t('preview')}</div>
                <div class="text-16">{$t('If you would like to view the questions in advance without affecting the results, you can start the preview.')}</div>
                <button class="text-blue-main text-16 font-bold mt-12" on:click="{() => {openPupilsNumber(), preview = true}}">
                    {$t('preview')}
                </button>
            </div>
        </div>
    </div>
    {#if (type == "prior" && !dashboard.lockPrior) || (type == "after" && !dashboard.lockAfter)}
    <div class="mt-32 md:mt-64 flex justify-center">
        <button class="bg-blue-main text-white py-10 px-24 rounded-md text-18" on:click="{openPupilsNumber}">
            {$t('check now')}
        </button>
    </div>
    {/if}
</div>
